if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.relatedProductsData = null;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        const relatedScript = this.querySelector('script[data-related-products]');
        if (relatedScript) {
          try {
            this.relatedProductsData = JSON.parse(relatedScript.textContent);
          } catch (e) {
            console.warn('Could not parse related products data:', e);
          }
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initAvailabilityCheck();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initAvailabilityCheck() {
        if (!this.relatedProductsData || !Array.isArray(this.relatedProductsData)) return;

        const variantSelects = this.variantSelectors;
        if (!variantSelects) return;

        const getSelectedSize = () => {
          const sizeInputs = variantSelects.querySelectorAll('input[type="radio"]:checked, select');
          for (const input of sizeInputs) {
            const name = (input.name || '').toLowerCase();
            if (name.includes('size') || name.includes('tamaño')) {
              return input.value || input.selectedOptions?.[0]?.value;
            }
          }
          return null;
        };

        const getSelectedColor = () => {
          const colorLinks = variantSelects.querySelectorAll('.product-variant-option__color.active');
          if (colorLinks.length > 0) {
            const href = colorLinks[0].getAttribute('href');
            if (href) {
              const product = this.relatedProductsData.find((p) => p.url === href);
              return product?.id || null;
            }
          }
          const currentUrl = window.location.pathname;
          const product = this.relatedProductsData.find((p) => p.url === currentUrl);
          return product?.id || null;
        };

        const updateColorAvailabilityForSize = (sizeValue) => {
          if (!sizeValue || !this.relatedProductsData || !Array.isArray(this.relatedProductsData)) return;
          const colorLinks = variantSelects.querySelectorAll('.product-variant-option__color');
          colorLinks.forEach((link) => {
            const href = link.getAttribute('href');
            if (!href) return;
            const product = this.relatedProductsData.find((p) => p.url === href);
            let isAvailable = false;
            if (product && Array.isArray(product.variants)) {
              const matched = product.variants.find((v) => v.option1 === sizeValue);
              isAvailable = !!(matched && matched.available);
            }
            link.classList.toggle('sold-out', !isAvailable);
          });
        };

        const updateSizeAvailabilityForColor = (colorProductId) => {
          if (!colorProductId || !this.relatedProductsData || !Array.isArray(this.relatedProductsData)) return;
          const product = this.relatedProductsData.find((p) => p.id === colorProductId);
          if (!product || !Array.isArray(product.variants)) return;

          const sizeInputs = variantSelects.querySelectorAll('input[type="radio"], select');
          sizeInputs.forEach((input) => {
            const name = (input.name || '').toLowerCase();
            if (!name.includes('size') && !name.includes('tamaño')) return;

            let sizeValue = null;
            if (input.tagName === 'SELECT') {
              sizeValue = input.value;
            } else if (input.type === 'radio') {
              sizeValue = input.value;
            }

            if (!sizeValue) return;

            const matched = product.variants.find((v) => v.option1 === sizeValue);
            const isAvailable = !!(matched && matched.available);

            if (input.tagName === 'SELECT') {
              const option = Array.from(input.options).find((opt) => opt.value === sizeValue);
              if (option) {
                option.classList.toggle('sold-out', !isAvailable);
              }
            } else {
              const label =
                input.closest('fieldset')?.querySelector(`label[for="${input.id}"]`) || input.nextElementSibling;
              if (label) {
                label.classList.toggle('sold-out', !isAvailable);
              }
            }
          });
        };

        this._updateColorAvailabilityForSize = updateColorAvailabilityForSize;
        this._updateSizeAvailabilityForColor = updateSizeAvailabilityForColor;
        this._getSelectedSize = getSelectedSize;
        this._getSelectedColor = getSelectedColor;

        const initialSize = getSelectedSize();
        const initialColor = getSelectedColor();
        if (initialSize) {
          updateColorAvailabilityForSize(initialSize);
        }
        if (initialColor) {
          updateSizeAvailabilityForColor(initialColor);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Ensure this product-info only handles its own variant selector events
        const variantSelector = target.closest('variant-selects');
        const expectedSelectorId = `variant-selects-${this.dataset.section}`;

        if (variantSelector && variantSelector.id !== expectedSelectorId) {
          console.log('Ignoring event from different variant selector:', variantSelector.id, 'expected:', expectedSelectorId);
          return;
        }

        this.resetProductFormState();

        // Always use this product-info's URL, not the target's URL
        const productUrl = this.dataset.url;

        console.log('Option value change for product-info:', this.dataset.section, {
          productUrl: productUrl,
          target: target,
          variantSelectorId: variantSelector?.id
        });

        
        this.checkAvailabilityOnVariantChange(target);

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = false; // Never swap products in Shop The Look
        const shouldFetchFullPage = false; this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      checkAvailabilityOnVariantChange(target) {
        if (!this.relatedProductsData || !Array.isArray(this.relatedProductsData)) return;
        if (!this._updateColorAvailabilityForSize || !this._updateSizeAvailabilityForColor) return;

        const targetName = (target.name || '').toLowerCase();
        const targetValue = target.value;

        if (targetName.includes('size') || targetName.includes('tamaño')) {
          if (this._updateColorAvailabilityForSize) {
            this._updateColorAvailabilityForSize(targetValue);
          }
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        // Always use the main section ID for fetching data from server
        // Server needs the main section ID (like 'rocket-shop-the-look') to process the request
        const sectionParam = this.sectionId;
        !shouldFetchFullPage && params.push(`section_id=${sectionParam}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        console.log('Request URL:', `${url}?${params.join('&')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // In Shop The Look, we need to find the correct product-info in the response
          // that matches the current product URL
          let responseProductInfo = html;

          if (this.dataset.originalSection) {
            // This is Shop The Look - find the product-info with matching data-url
            const allProductInfos = html.querySelectorAll('product-info');
            const matchingProductInfo = Array.from(allProductInfos).find(pi =>
              pi.dataset.url === this.dataset.url
            );

            if (matchingProductInfo) {
              responseProductInfo = matchingProductInfo;
              console.log('Found matching product-info in response for URL:', this.dataset.url);
            } else {
              console.warn('No matching product-info found in response for URL:', this.dataset.url);
              return;
            }
          }

          const variant = this.getSelectedVariant(responseProductInfo);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(responseProductInfo);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          requestAnimationFrame(() => {
            this.initAvailabilityCheck();
          });

          this.updateMedia(responseProductInfo, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            // For Shop The Look, both source and destination use the same block ID
            const sourceId = this.dataset.originalSection ? this.dataset.section : this.sectionId;
            const source = responseProductInfo.querySelector(`#${id}-${sourceId}`) || html.getElementById(`${id}-${sourceId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            console.log(`Looking for: ${id}-${sourceId} in response, ${id}-${this.dataset.section} in DOM`);
            console.log('Source found:', !!source, 'Destination found:', !!destination);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('product-price');
          // Additional price update for Shop The Look
          if (this.dataset.originalSection) {
            const priceSourceElement = responseProductInfo.querySelector('.lookbook-card__product-price') ||
              responseProductInfo.querySelector('.price');
            const priceDestElement = this.querySelector(`#price-${this.dataset.section}`);

            if (priceSourceElement && priceDestElement) {
              priceDestElement.innerHTML = priceSourceElement.innerHTML;
              console.log('Updated price element directly');
            } else {
              // Fallback: update price manually with variant data
              this.updatePriceWithVariant(variant);
            }
          }

          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.dataset.section, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Find the submit button in the response HTML
          const submitButtonInResponse = responseProductInfo.querySelector(`#ProductSubmitButton-${this.dataset.section}`) ||
            html.getElementById(`ProductSubmitButton-${this.dataset.section}`);

          if (submitButtonInResponse) {
            const shouldDisable = submitButtonInResponse.hasAttribute('disabled');
            const buttonText = submitButtonInResponse.querySelector('span')?.textContent || window.variantStrings.addToCart;

            this.productForm?.toggleSubmitButton(shouldDisable, buttonText);
          } else {
            // If button not found in response, enable by default
            this.productForm?.toggleSubmitButton(false, window.variantStrings.addToCart);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        // For single product pages
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // For multi-product sections like shop-the-look
        this.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        if (!this.quantityForm) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step', 'data-inventory', 'max'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      updatePriceWithVariant(variant) {
        if (!variant) return;

        const priceElement = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceElement) return;

        const priceContainer = priceElement.querySelector('.price');
        if (!priceContainer) return;

        // Update price display
        const priceDisplay = priceContainer.querySelector('.price__regular .price-item--regular');
        const comparePriceDisplay = priceContainer.querySelector('.price__sale .price-item--sale');
        const compareAtPriceDisplay = priceContainer.querySelector('.price__sale .price-item--regular');

        if (priceDisplay) {
          const formattedPrice = this.formatMoney(variant.price);
          priceDisplay.textContent = formattedPrice;
        }

        // Handle sale pricing
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceContainer.classList.add('price--on-sale');
          priceContainer.classList.remove('price--sold-out');

          if (comparePriceDisplay) {
            comparePriceDisplay.textContent = this.formatMoney(variant.price);
          }
          if (compareAtPriceDisplay) {
            compareAtPriceDisplay.textContent = this.formatMoney(variant.compare_at_price);
          }
        } else {
          priceContainer.classList.remove('price--on-sale');
        }

        // Handle sold out
        if (!variant.available) {
          priceContainer.classList.add('price--sold-out');
        } else {
          priceContainer.classList.remove('price--sold-out');
        }

        console.log('Updated price manually with variant:', variant.id);
      }

      formatMoney(price) {
        return window.Shopify?.formatMoney ?
          window.Shopify.formatMoney(price) :
          `$${(price / 100).toFixed(2)}`;
      }
    }
  );
}
